/**
 * When io.ai is asked to push, it always asks first: this card sits above
 * the composer with the same verdicts the push sheet shows, and settles into
 * a one-line record once the push is done or cancelled.
 */
import type { FC } from "react";

import type { PushPlan } from "../../tender/useQuestions";

export type ChatPushState =
  | { phase: "planning"; count: number }
  | { phase: "confirm"; plan: PushPlan }
  | { phase: "pushing"; plan: PushPlan }
  | { phase: "done"; text: string; failed: boolean };

const VERDICT: Record<string, string> = {
  unchanged: "unchanged",
  changed: "changed",
  newer: "newer in mytender",
  new: "new",
  skipped: "no text, skipped",
  missing: "not in document, skipped",
};

export const ChatPushCard: FC<{
  state: ChatPushState;
  onConfirm: () => void;
  onCancel: () => void;
  onDismiss: () => void;
}> = ({ state, onConfirm, onCancel, onDismiss }) => {
  if (state.phase === "done") {
    return (
      <div className="mb-2 flex items-center gap-2 rounded-md border border-line bg-subtle px-3 py-2 text-xs text-fg-2">
        <span
          className={state.failed ? "size-1.5 rounded-full bg-danger" : "size-1.5 rounded-full bg-status-complete"}
        />
        <span className="min-w-0 flex-1">{state.text}</span>
        <button type="button" className="font-semibold text-fg-3 hover:text-fg" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    );
  }
  if (state.phase === "planning") {
    return (
      <div className="mb-2 rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-fg-3">
        Checking {state.count ? `${state.count} question${state.count === 1 ? "" : "s"}` : "the questions"} in the
        document…
      </div>
    );
  }
  const rows = state.plan.rows;
  const toPush = rows.filter((r) => r.include);
  return (
    <div className="mb-2 flex flex-col gap-2 rounded-md border border-line bg-surface px-3 py-2.5 shadow-pop">
      <span className="font-semibold">
        {toPush.length
          ? `Send ${toPush.length} question${toPush.length === 1 ? "" : "s"} to mytender?`
          : "Nothing to send"}
      </span>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-fg-2">
        {rows.slice(0, 8).map((r) => (
          <FragmentRow
            key={r.question.section_id}
            n={r.question.section_number}
            text={`${r.question.heading} · ${VERDICT[r.verdict]}${r.live?.editors.length ? ` · ${r.live.editors.join(", ")} editing now` : ""}`}
          />
        ))}
        {rows.length > 8 && <FragmentRow n="" text={`and ${rows.length - 8} more`} />}
      </div>
      <span className="text-xs text-fg-3">
        Each becomes the current version. Only Not started questions change status.
      </span>
      <div className="flex justify-end gap-1.5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-sm px-2.5 py-1 text-xs font-semibold text-fg-2 hover:bg-subtle"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!toPush.length || state.phase === "pushing"}
          onClick={onConfirm}
          className="rounded-sm bg-fg px-2.5 py-1 text-xs font-semibold text-fg-inverse disabled:opacity-40"
        >
          {state.phase === "pushing" ? "Sending…" : `Send ${toPush.length}`}
        </button>
      </div>
    </div>
  );
};

const FragmentRow: FC<{ n: string; text: string }> = ({ n, text }) => (
  <>
    <b className="font-semibold tabular-nums text-fg-3">{n}</b>
    <span className="truncate">{text}</span>
  </>
);
